/**
 * Pure helpers for the Reports list. No React / no I/O so they are directly
 * unit-testable (see reports-list-helpers.test.ts).
 */
import type {
  ClientReportSummary,
  ReportStatus,
} from '../../lib/api/client-reports/client-reports-api';
import { englishT, type Translate } from './agents/agent-helpers';

/**
 * The only statuses a human still has to act on. Mirrors the backend's
 * `ClientReport::REVIEWABLE_STATUSES`.
 */
export const REVIEW_STATUSES: string[] = [
  'pending_review',
  'changes_requested',
];

/**
 * Everything that is NOT awaiting review is "past" — deliberately an allowlist
 * for review rather than an enumeration of past statuses.
 *
 * `approved` is transient: the backend's PublishApprovedReport listener flips
 * approved → published synchronously, so a row sitting at `approved` is by
 * definition stuck and must still surface (under Past) rather than vanish. The
 * same fall-through means a status added later lands in Past instead of
 * nagging forever in a bucket the user cannot action.
 */
export function needsReview(report: { status: string }): boolean {
  return REVIEW_STATUSES.includes(report.status);
}

export interface PartitionedReports {
  toCheck: ClientReportSummary[];
  past: ClientReportSummary[];
}

/**
 * Split the list into the two buckets in one pass, preserving the backend's
 * `period_start DESC, created_at DESC` ordering within each.
 */
export function partitionReports(
  reports: ClientReportSummary[]
): PartitionedReports {
  const toCheck: ClientReportSummary[] = [];
  const past: ClientReportSummary[] = [];
  for (const r of reports) {
    (needsReview(r) ? toCheck : past).push(r);
  }
  return { toCheck, past };
}

/** Human labels only — the colours live in `.client-reports__tag--{status}`. */
export const STATUS_LABEL: Record<string, string> = {
  pending_review: 'Pending review',
  changes_requested: 'Changes requested',
  approved: 'Approved',
  published: 'Published',
  rejected: 'Rejected',
  archived: 'Archived',
};

export function statusLabel(status: string): string {
  return STATUS_LABEL[status] || STATUS_LABEL.pending_review;
}

/**
 * The status tag text, sentence case ("Pending review"), in the language of
 * the translator it is handed (English without one). A status this page does
 * not know yet is shown as the API names it, underscores as spaces and the
 * first letter upper-cased.
 */
export function statusTagLabel(
  status: string,
  t: Translate = englishT
): string {
  if (STATUS_LABEL[status]) {
    return t(`ReportStatus_${status}`, STATUS_LABEL[status]);
  }
  const words = String(status ?? '')
    .replace(/_/g, ' ')
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const isEnglish = (language?: string): boolean =>
  !language || /^en\b/i.test(language);

/**
 * The month's full name in the given app language ("juli" for `nl`). English,
 * no language and a language tag the runtime does not know give the English
 * name.
 */
export function monthName(month0: number, language?: string): string {
  if (isEnglish(language)) return MONTHS_LONG[month0];
  try {
    return new Intl.DateTimeFormat(language, {
      month: 'long',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(2020, month0, 1)));
  } catch {
    return MONTHS_LONG[month0];
  }
}

const MONTH_WORD = new RegExp(`\\b(${MONTHS_LONG.join('|')})\\b`, 'g');

const upperFirst = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

/**
 * The server writes `period_label` in English ("July 2026"). Swap its month
 * names for the app language's and keep the rest (year, quarter) as sent.
 */
function localizeMonths(label: string, language?: string): string {
  if (isEnglish(language)) return label;
  return upperFirst(
    label.replace(MONTH_WORD, (name) =>
      monthName(MONTHS_LONG.indexOf(name), language)
    )
  );
}

/** `YYYY-MM-DD` → [year, monthIndex0] without timezone drift from Date parsing. */
function parseYmd(value: string): { year: number; month0: number } | null {
  const m = /^(\d{4})-(\d{2})/.exec(value.trim());
  if (!m) return null;
  const month0 = Number(m[2]) - 1;
  if (month0 < 0 || month0 > 11) return null;
  return { year: Number(m[1]), month0 };
}

/**
 * The headline label for a report block ("July"), with month names in the app
 * language. Prefers the server's `period_label`; falls back to deriving one
 * from `period_start`.
 *
 * Both fields are nullable on the wire and the block is a clickable control, so
 * this ALWAYS returns a non-empty string — an unlabelled control is worse than
 * a generic one.
 */
export function periodLabel(
  report: {
    period_label?: string | null;
    period_start?: string | null;
    period_type?: string | null;
  },
  language?: string,
  t: Translate = englishT
): string {
  const label = (report.period_label || '').trim();
  if (label !== '') return localizeMonths(label, language);

  const start = (report.period_start || '').trim();
  if (start !== '') {
    const parsed = parseYmd(start);
    if (parsed) {
      const { year, month0 } = parsed;
      const month = upperFirst(monthName(month0, language));
      if (report.period_type === 'monthly') return month;
      if (report.period_type === 'quarterly') {
        return `Q${Math.floor(month0 / 3) + 1} ${year}`;
      }
      // weekly / custom / unknown — the month + year is the honest summary.
      return `${month} ${year}`;
    }
    return start;
  }

  return t('ClientReportsPeriodFallback', 'Report');
}

/**
 * `2026-07-16T…` → `16 Jul 2026`, or `16 jul 2026` for `nl`: day, short month
 * and year in the app language. Empty string when unparseable.
 */
export function formatDate(
  value: string | null | undefined,
  language?: string
): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  };
  try {
    // English keeps the day-first form it always had.
    return d.toLocaleDateString(
      isEnglish(language) ? 'en-GB' : language,
      options
    );
  } catch {
    return d.toLocaleDateString('en-GB', options);
  }
}

export type { ReportStatus };
